import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit, FiTrash2, FiX, FiZap } from 'react-icons/fi';
import { toast } from 'react-toastify';
import './StripeAutomation.css';

const StripeAutomation = ({ isOpen, onClose, onSave, automation = null, stripeAccounts = [], extensions = [] }) => {
  const [formData, setFormData] = useState({
    automationName: '',
    stripeAccountId: '',
    stripePlanIds: '',
    extensionIds: []
  });

  useEffect(() => {
    if (automation) {
      setFormData({
        automationName: automation.automationName || '',
        stripeAccountId: automation.stripeAccountId || '',
        stripePlanIds: automation.stripePlanIds || '',
        extensionIds: automation.extensionIds || []
      });
    } else {
      setFormData({
        automationName: '',
        stripeAccountId: '',
        stripePlanIds: '',
        extensionIds: []
      });
    }
  }, [automation]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleExtensionChange = (extensionId) => {
    setFormData(prev => ({
      ...prev,
      extensionIds: prev.extensionIds.includes(extensionId)
        ? prev.extensionIds.filter(id => id !== extensionId)
        : [...prev.extensionIds, extensionId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.automationName.trim()) {
      toast.error('Automation name is required');
      return;
    }
    
    if (!formData.stripeAccountId) {
      toast.error('Please select a Stripe account');
      return;
    }
    
    if (!formData.stripePlanIds.trim()) {
      toast.error('Stripe Plan IDs are required');
      return;
    }
    
    if (formData.extensionIds.length === 0) {
      toast.error('Please select at least one extension');
      return;
    }

    try {
      await onSave(formData);
      toast.success(automation ? 'Automation updated successfully' : 'Automation created successfully');
      onClose();
    } catch (error) {
      console.error('Error saving automation:', error);
      toast.error('Failed to save automation');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content automation-modal">
        <div className="modal-header">
          <h3>
            <FiZap />
            {automation ? 'Edit Stripe Automation' : 'Add New Stripe Automation'}
          </h3>
          <button className="modal-close" onClick={onClose}>
            <FiX />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="automation-form">
          <div className="form-group">
            <label htmlFor="automationName">Automation Name *</label>
            <input
              type="text"
              id="automationName"
              name="automationName"
              value={formData.automationName}
              onChange={handleInputChange}
              placeholder="Enter automation name (e.g., Premium Plan Automation)"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="stripeAccountId">Stripe Account *</label>
            <select
              id="stripeAccountId"
              name="stripeAccountId"
              value={formData.stripeAccountId}
              onChange={handleInputChange}
              required
            >
              <option value="">Select a Stripe Account</option>
              {stripeAccounts.map(account => (
                <option key={account.id} value={account.id}>
                  {account.accountName}
                </option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="stripePlanIds">Stripe Plan IDs *</label>
            <textarea
              id="stripePlanIds"
              name="stripePlanIds"
              value={formData.stripePlanIds}
              onChange={handleInputChange}
              placeholder="Enter Stripe Plan IDs (one per line or comma-separated)&#10;Example:&#10;price_1234567890&#10;price_0987654321"
              rows="4"
              required
            />
            <small className="form-help">
              Enter the Stripe Plan IDs that should trigger this automation. One per line or comma-separated.
            </small>
          </div>
          
          <div className="form-group">
            <label>Extensions to Create *</label>
            <div className="extensions-grid">
              {extensions.map(extension => (
                <label key={extension._id} className="extension-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.extensionIds.includes(extension._id)}
                    onChange={() => handleExtensionChange(extension._id)}
                  />
                  <span className="checkbox-label">
                    {extension.name}
                  </span>
                </label>
              ))}
            </div>
            <small className="form-help">
              Select which extensions should be created when this automation is triggered.
            </small>
          </div>
          
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              {automation ? <><FiEdit /> Update Automation</> : <><FiPlus /> Create Automation</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StripeAutomation;
